import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, GitCompare, FileText } from "lucide-react";

// Human-readable labels for the 26 clinical flags
const FIELD_LABELS = {
  failed_euploid: "Failed euploid transfers",
  failed_untested: "Failed untested transfers",
  no_pgta: "No PGT-A performed",
  high_sdf: "High Sperm DNA Fragmentation",
  male_karyo: "Abnormal paternal karyotype",
  severe_oat: "Severe OAT / poor morphokinetics",
  era_displaced: "Displaced window of implantation",
  ce_cd138: "Chronic endometritis (CD138+)",
  endo_tb: "Latent endometrial TB",
  microbiome: "Endometrial dysbiosis",
  anatomy: "Cavity anomaly / adenomyosis",
  th1_th2: "Th1/Th2 imbalance",
  asri_nk: "Elevated NK / ANA / APA",
  kir_mismatch: "High-risk KIR genotype",
  hla_antibodies: "HLA antibodies / DSA",
  hla_crossmatch: "Paternal HLA cross-match",
  coeliac: "Coeliac disease",
  chronic_inflammation: "Chronic inflammation",
  hyperhomocysteinemia: "Hyperhomocysteinemia (couple)",
  vit_d_deficiency: "Vitamin D deficiency",
  thyroid_autoimm: "Thyroid autoimmunity",
  insulin_resistance: "Insulin resistance",
  hyperprolactinemia: "Hyperprolactinemia",
  inherited_thrombophilia: "Inherited thrombophilia",
  endometriosis_occult: "Occult endometriosis",
  seminal_ros: "Seminal oxidative stress",
};

const fmt = (v) => {
  if (typeof v === "boolean") return v ? "Yes" : "—";
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
};

export default function Compare() {
  const [sp] = useSearchParams();
  const a = sp.get("a"); const b = sp.get("b");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!a || !b) return;
    api.post("/cases/compare", { case_ids: [a, b] })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [a, b]);

  if (loading) return <div className="text-sm text-slate-500">Loading comparison…</div>;
  if (!data) return (
    <div className="text-sm text-slate-500">
      Comparison could not be loaded. <Link to="/cases" className="text-blue-900 font-semibold">Back to cases</Link>
    </div>
  );

  const diffKeys = new Set(data.diffs.map((d) => d.field));
  const allKeys = Object.keys(FIELD_LABELS);

  return (
    <div className="reveal">
      <Link to="/cases" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} /> Back to cases
      </Link>

      <div className="mt-4 flex items-center gap-3">
        <GitCompare className="h-6 w-6 text-blue-900" strokeWidth={1.5} />
        <div>
          <div className="eyebrow">Case comparison</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Side-by-side diff</h1>
        </div>
      </div>

      {/* Case headers */}
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {[data.a, data.b].map((c, i) => (
          <div key={c.id} className={`border rounded-md p-4 bg-white ${i === 0 ? "border-blue-900" : "border-teal-700"}`}>
            <div className="flex items-center justify-between">
              <div className="eyebrow" style={{ color: i === 0 ? "#1E3A8A" : "#0F766E" }}>Case {i === 0 ? "A" : "B"}</div>
              <Link to={`/workflow/${c.id}`} className="text-xs text-slate-500 hover:text-slate-900">Open →</Link>
            </div>
            <div className="mt-1 font-semibold text-slate-900 truncate">{c.patient_label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{new Date(c.created_at).toLocaleString()}</div>
            <div className="mt-2 text-xs text-slate-600 font-mono line-clamp-3">{c.protocol?.diagnosis_summary}</div>
          </div>
        ))}
      </div>

      {/* Diffs summary */}
      <div className="mt-6 border-l-4 border-blue-900 bg-blue-50 rounded-sm p-3 text-sm text-slate-800" data-testid="diff-summary">
        <b>{data.diffs.length}</b> input{data.diffs.length === 1 ? "" : "s"} differ between the two cases.
        {data.diffs.length === 0 && " Inputs are identical."}
      </div>

      {/* Full input diff table */}
      <div className="mt-6 border border-slate-200 rounded-md bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="diff-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Clinical input</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Case A</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-700">Case B</th>
            </tr>
          </thead>
          <tbody>
            {allKeys.map((k) => {
              const isDiff = diffKeys.has(k);
              const va = data.a.inputs[k]; const vb = data.b.inputs[k];
              return (
                <tr key={k} className={isDiff ? "bg-amber-50" : "border-t border-slate-100"} data-testid={`diff-row-${k}${isDiff ? "-changed" : ""}`}>
                  <td className="px-4 py-2 text-slate-700">
                    {FIELD_LABELS[k]}
                    {isDiff && <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-amber-800">Changed</span>}
                  </td>
                  <td className="px-4 py-2 font-mono text-slate-900">{fmt(va)}</td>
                  <td className="px-4 py-2 font-mono text-slate-900">{fmt(vb)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Protocol section counts side-by-side */}
      <div className="mt-8">
        <div className="eyebrow">Generated protocol summary</div>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          {[data.a, data.b].map((c, i) => (
            <div key={c.id} className="border border-slate-200 rounded-md p-4 bg-white">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-900" strokeWidth={1.75} />
                <span className="font-semibold text-slate-900">Case {i === 0 ? "A" : "B"} · items per section</span>
              </div>
              <ul className="mt-3 space-y-1 text-sm text-slate-700">
                {(c.protocol?.sections || []).map((s) => (
                  <li key={s.key} className="flex items-center justify-between border-b border-slate-100 py-1">
                    <span>{s.title}</span>
                    <span className="font-mono text-slate-900">{(s.items || []).length}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
